import { useEffect } from 'react';
import { Text } from '@belong/ui';
import type {
  ConsumerFailedPaymentModel,
  ExternalPaymentMethod,
  ConsumerPaymentMethodSummaryModel,
  ConsumerWireInstructionsModel,
} from '../failed-payments.types';
import type { PaymentAccountResourceModel } from 'api/models';
import { format } from 'date-fns';
import { PaymentPreference } from 'models/enums';
import { FAILED_PAYMENTS_STRINGS } from '../failed-payments-strings';
import { mapPaymentAccountToPaymentMethodSummary } from '../failed-payments-utils';
import { FailedPaymentMethodSelector } from '../failed-payment-method-selector';
import { WireDetails } from './homeowner-change-payment-method-subsection';

type RetryTodaySubsectionProps = {
  failedPayments: ConsumerFailedPaymentModel[];
  paymentMethods: PaymentAccountResourceModel[];
  paymentAccountUniqueId: string | null;
  paymentMethod: ConsumerPaymentMethodSummaryModel | null | undefined;
  onSelectPaymentMethod: (id: string) => void;
  canPayByWire?: boolean;
  externalPaymentMethod: ExternalPaymentMethod | null;
  canPayByZelle: boolean;
  onSelectExternalPaymentMethod: (method: ExternalPaymentMethod) => void;
  wireInstructions?: ConsumerWireInstructionsModel | null;
  wireInstructionsLoading?: boolean;
  wireInstructionsError?: boolean;
  onRetryWireInstructions?: () => void;
  wireCoveringBalanceUniqueIds?: string[];
  onToggleWireCovering?: (balanceUniqueIds: string[]) => void;
  wireReceiptFile?: File | null;
  onWireReceiptChange?: (file: File | null) => void;
};

export function RetryTodaySubsection({
  failedPayments,
  paymentMethods,
  paymentAccountUniqueId,
  paymentMethod,
  onSelectPaymentMethod,
  canPayByWire = false,
  externalPaymentMethod,
  canPayByZelle,
  onSelectExternalPaymentMethod,
  wireInstructions = null,
  wireInstructionsLoading = false,
  wireInstructionsError,
  onRetryWireInstructions,
  wireCoveringBalanceUniqueIds = [],
  onToggleWireCovering,
  wireReceiptFile = null,
  onWireReceiptChange,
}: RetryTodaySubsectionProps) {
  const availablePaymentMethods = paymentMethods.filter(
    (method) => method.accountId && method.isVerified,
  );
  const selectedPaymentMethod =
    availablePaymentMethods.find(
      (method) => method.accountId === paymentAccountUniqueId,
    ) ?? availablePaymentMethods[0];
  const selectedPaymentMethodSummary = selectedPaymentMethod
    ? mapPaymentAccountToPaymentMethodSummary(selectedPaymentMethod)
    : paymentMethod;
  const retryDateLabel = format(new Date(), 'MMM d, yyyy');

  useEffect(() => {
    if (externalPaymentMethod) return;
    if (!paymentAccountUniqueId && selectedPaymentMethod?.accountId) {
      onSelectPaymentMethod(selectedPaymentMethod.accountId);
    }
  }, [
    onSelectPaymentMethod,
    paymentAccountUniqueId,
    externalPaymentMethod,
    selectedPaymentMethod?.accountId,
  ]);

  const hasSelectableMethod = Boolean(selectedPaymentMethodSummary);

  return (
    <div className="flex w-full flex-col gap-2xl">
      <div
        className={
          externalPaymentMethod
            ? 'flex w-full flex-col gap-sm'
            : 'flex w-full flex-col gap-2xl'
        }
      >
        <div className="flex flex-col gap-xs">
          <Text
            variant="h3"
            fontWeight="semibold"
            className={
              externalPaymentMethod ? 'text-h3-xl leading-[normal]' : undefined
            }
          >
            {externalPaymentMethod
              ? FAILED_PAYMENTS_STRINGS[
                  externalPaymentMethod === 'ZelleTransfer'
                    ? 'retry_today.zelle_prompt'
                    : 'retry_today.wire_prompt'
                ]
              : hasSelectableMethod
                ? FAILED_PAYMENTS_STRINGS['retry_today.prompt']
                : FAILED_PAYMENTS_STRINGS['retry_today.no_method_prompt']}
          </Text>
          <Text
            variant="body"
            className={externalPaymentMethod ? 'leading-[20px]' : undefined}
          >
            {externalPaymentMethod
              ? FAILED_PAYMENTS_STRINGS['retry_today.wire_description']
              : hasSelectableMethod
                ? `${FAILED_PAYMENTS_STRINGS['retry_today.rescheduled_for']} ${retryDateLabel}`
                : FAILED_PAYMENTS_STRINGS['retry_today.no_method_description']}
          </Text>
        </div>

        <FailedPaymentMethodSelector
          paymentMethod={selectedPaymentMethodSummary}
          selectedPaymentAccountUniqueId={paymentAccountUniqueId}
          paymentType={PaymentPreference.Rent}
          onSelectPaymentMethod={onSelectPaymentMethod}
          externalPaymentMethod={externalPaymentMethod}
          onSelectWireTransfer={
            canPayByWire
              ? () => onSelectExternalPaymentMethod('WireTransfer')
              : undefined
          }
          onSelectZelleTransfer={
            canPayByZelle
              ? () => onSelectExternalPaymentMethod('ZelleTransfer')
              : undefined
          }
        />
      </div>
      {externalPaymentMethod && onToggleWireCovering && onWireReceiptChange && (
        <WireDetails
          key={externalPaymentMethod}
          externalPaymentMethod={externalPaymentMethod}
          failedPayments={failedPayments}
          wireInstructions={wireInstructions}
          wireInstructionsLoading={wireInstructionsLoading}
          wireInstructionsError={wireInstructionsError}
          onRetryWireInstructions={onRetryWireInstructions}
          wireCoveringBalanceUniqueIds={wireCoveringBalanceUniqueIds}
          onToggleWireCovering={onToggleWireCovering}
          wireReceiptFile={wireReceiptFile}
          onWireReceiptChange={onWireReceiptChange}
        />
      )}
    </div>
  );
}
